import type { ReactNode } from 'react';
import React, { useState, useCallback } from 'react';
import { MultiSelect, Text, Loader, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import type { AbstractSingleFilterDropdownProps } from '../../SingleFilterType';
import { GenericFilterBody } from '../GenericFilter/GenericFilterBody';
import { GenericFilterDropdown } from '../GenericFilter/GenericFilterDropdown';
import { GenericFilterFooter } from '../GenericFilter/GenericFilterFooter';
import {
  LargeListFilterOperation,
  FunnelStepLargeListFilterOperation,
} from './GenericLargeListType';
import type { LargeListFilterValue, BasicListOption } from './GenericLargeListType';
import { FilterItemRadio, FilterRadioContainer } from '../GenericSubElement/FilterItemRadio';
import { GenericFilterHeader } from '../GenericFilter/GenericFilterHeader';

const DROPDOWN_WIDTH = 316;

const SelectContainer = styled.div`
  margin-left: ${tokens.spacer.x3};
`;

export const readableLargeListOperations: Record<LargeListFilterOperation, string> = {
  [LargeListFilterOperation.IS_ANY_OF_THESE]: 'is any of these',
  [LargeListFilterOperation.IS_NONE_OF_THESE]: 'is none of these',
};

enum ValidationErrorEnum {
  SELECT_ONE_OPERATION = 'Select one option',
  SELECT_ONE_OPTION = 'Select at least one option',
  OPTION_IS_LOADING = 'Please wait while the list loads',
}

function getFilterValueErrors<T>(
  operation?: LargeListFilterOperation,
  selectedOptions?: BasicListOption<T>[],
  optionListIsLoading?: boolean,
): ValidationErrorEnum | undefined {
  if (!operation) {
    return ValidationErrorEnum.SELECT_ONE_OPERATION;
  }
  if (optionListIsLoading === true) {
    return ValidationErrorEnum.OPTION_IS_LOADING;
  }
  if (!selectedOptions || selectedOptions.length === 0) {
    return ValidationErrorEnum.SELECT_ONE_OPTION;
  }
  return undefined;
}

interface GenericLargeListDropdownProps<T>
  extends AbstractSingleFilterDropdownProps<LargeListFilterValue<T>> {
  optionMap: Map<T, BasicListOption<T>>;
  optionListIsLoading?: boolean;
  header?: ReactNode;
  placeholder?: string;
  isFunnelStep?: boolean;
}

const DEFAULT_OPERATION = LargeListFilterOperation.IS_ANY_OF_THESE;

export function GenericLargeListDropdown<T extends string>({
  currentValue,
  optionalDefaultValue,
  onApply,
  optionListIsLoading,
  optionMap,
  filterName,
  header,
  placeholder = 'Select items',
  isFunnelStep,
}: GenericLargeListDropdownProps<T>): JSX.Element {
  const [dirtyOperation, setDirtyOperation] = useState<LargeListFilterOperation | undefined>(
    currentValue?.operation ?? optionalDefaultValue?.operation ?? DEFAULT_OPERATION,
  );
  const [dirtySelectedOptions, setDirtySelectedOptions] = useState<BasicListOption<T>[]>(
    (currentValue?.selectedValues ?? optionalDefaultValue?.selectedValues ?? [])?.map((item) => {
      return optionMap.get(item) as BasicListOption<T>;
    }),
  );
  const [validationError, setValidationError] = useState<ValidationErrorEnum>();
  const handleMultiSelectChange = useCallback(
    (changedValue) => {
      setDirtySelectedOptions(changedValue);
      setValidationError(undefined);
    },
    [setDirtySelectedOptions, setValidationError],
  );

  const handleOperationChange = useCallback(
    (changedValue) => {
      setDirtyOperation(changedValue);
      setValidationError(undefined);
    },
    [setDirtyOperation, setValidationError],
  );

  const handleApply = useCallback(() => {
    const filterValueErrors = getFilterValueErrors(
      dirtyOperation,
      dirtySelectedOptions,
      optionListIsLoading,
    );
    if (filterValueErrors) {
      setValidationError(filterValueErrors);
    } else if (dirtyOperation) {
      const selectedValues = dirtySelectedOptions.map((item) => item.value);
      onApply({
        operation: dirtyOperation,
        selectedValues,
      });
    }
  }, [onApply, dirtyOperation, dirtySelectedOptions, optionListIsLoading]);

  return (
    <GenericFilterDropdown
      ariaLabel={`${filterName} dropdown`}
      customWidth={DROPDOWN_WIDTH}
      onApply={handleApply}
    >
      {header != null && <GenericFilterHeader>{header}</GenericFilterHeader>}
      <GenericFilterBody>
        <FilterRadioContainer>
          {Object.values(
            isFunnelStep === true ? FunnelStepLargeListFilterOperation : LargeListFilterOperation,
          ).map((optionOperation) => {
            return (
              <React.Fragment key={optionOperation}>
                <FilterItemRadio
                  isChecked={optionOperation === dirtyOperation}
                  id={`${filterName}-dropdown-${optionOperation}`}
                  name={optionOperation}
                  onChange={() => handleOperationChange(optionOperation)}
                  value={`${optionOperation}`}
                  labelContent={
                    readableLargeListOperations[optionOperation as LargeListFilterOperation]
                  }
                  isInvalid={validationError === ValidationErrorEnum.SELECT_ONE_OPERATION}
                />
                {dirtyOperation && optionOperation === dirtyOperation && (
                  <SelectContainer>
                    {optionListIsLoading === true && <Loader size="l" isGreyscale />}
                    {optionListIsLoading !== true && (
                      <MultiSelect
                        availableOptions={Array.from(optionMap.values())}
                        selectedOptions={dirtySelectedOptions}
                        onChange={handleMultiSelectChange}
                        placeholder={placeholder}
                        isInvalid={validationError === ValidationErrorEnum.SELECT_ONE_OPTION}
                      />
                    )}
                    {validationError === ValidationErrorEnum.SELECT_ONE_OPTION && (
                      <Text textColor="text_error">{validationError}</Text>
                    )}
                  </SelectContainer>
                )}
              </React.Fragment>
            );
          })}
        </FilterRadioContainer>
        {(validationError === ValidationErrorEnum.SELECT_ONE_OPERATION ||
          validationError === ValidationErrorEnum.OPTION_IS_LOADING) && (
          <Text textColor="text_error">{validationError}</Text>
        )}
      </GenericFilterBody>
      <GenericFilterFooter />
    </GenericFilterDropdown>
  );
}
